/**
 * IndexedDB access. Ciphertext in, ciphertext out — this module has no idea
 * what any of it means and holds no keys.
 */

import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { AnchorRecord, VaultMetaRecord } from './types.js';

const DB_NAME = 'recovery-vault';
const DB_VERSION = 1;

interface VaultDB extends DBSchema {
  meta: {
    key: string;
    value: VaultMetaRecord;
  };
  anchors: {
    key: string;
    value: AnchorRecord;
  };
}

let dbPromise: Promise<IDBPDatabase<VaultDB>> | null = null;

function db(): Promise<IDBPDatabase<VaultDB>> {
  dbPromise ??= openDB<VaultDB>(DB_NAME, DB_VERSION, {
    upgrade(database) {
      if (!database.objectStoreNames.contains('meta')) {
        database.createObjectStore('meta', { keyPath: 'id' });
      }
      if (!database.objectStoreNames.contains('anchors')) {
        database.createObjectStore('anchors', { keyPath: 'id' });
      }
    },
    blocking() {
      // Another tab is upgrading. Let go rather than wedge it.
      void db().then((d) => d.close());
      dbPromise = null;
    },
  });
  return dbPromise;
}

export async function readMeta(): Promise<VaultMetaRecord | undefined> {
  return (await db()).get('meta', 'meta');
}

export async function writeMeta(meta: VaultMetaRecord): Promise<void> {
  await (await db()).put('meta', meta);
}

export async function listAnchorRecords(): Promise<AnchorRecord[]> {
  return (await db()).getAll('anchors');
}

export async function readAnchorRecord(id: string): Promise<AnchorRecord | undefined> {
  return (await db()).get('anchors', id);
}

export async function writeAnchorRecord(record: AnchorRecord): Promise<void> {
  await (await db()).put('anchors', record);
}

export async function deleteAnchorRecord(id: string): Promise<void> {
  await (await db()).delete('anchors', id);
}

/**
 * Replaces the whole vault in one transaction.
 *
 * Import must be all-or-nothing: a half-restored vault on a new phone is worse
 * than no vault, because the dashboard would look healthy while some anchors
 * silently had no hints behind them.
 */
export async function replaceAll(
  meta: VaultMetaRecord,
  anchors: AnchorRecord[],
): Promise<void> {
  const database = await db();
  const tx = database.transaction(['meta', 'anchors'], 'readwrite');
  await tx.objectStore('anchors').clear();
  await tx.objectStore('meta').put(meta);
  for (const anchor of anchors) {
    await tx.objectStore('anchors').put(anchor);
  }
  await tx.done;
}

/** Wipes the local vault. Used by "forget this device" and account deletion. */
export async function destroyVault(): Promise<void> {
  const database = await db();
  const tx = database.transaction(['meta', 'anchors'], 'readwrite');
  await tx.objectStore('meta').clear();
  await tx.objectStore('anchors').clear();
  await tx.done;
}

/** For tests: drop the cached connection so a fresh fake-indexeddb is picked up. */
export function resetConnectionForTests(): void {
  dbPromise = null;
}
